import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { Dropdown as DsDropdown } from '@digdir/designsystemet-react'
import { Icon, type IconName } from './Icon'

// ---------------------------------------------------------------------------
// DS owns the native manual-popover surface; this wrapper owns coordinate
// placement, region invocation and roving focus. Labels/callbacks belong to callers.
// ---------------------------------------------------------------------------

export interface MenuItem {
  /** Stable key — also the React list key. */
  key: string
  /** Visible label (pre-translated by the caller). */
  label: string
  /** Optional leading icon. */
  icon?: IconName
  /** Invoked when the item is chosen; the menu then closes. */
  onSelect: () => void
  /** When true the item is shown but cannot be activated or focused. */
  disabled?: boolean
  /** 'danger' tints the item with the danger token. Defaults to 'default'. */
  tone?: 'default' | 'danger'
  /** Render a separator above this item. */
  separatorBefore?: boolean
}

export interface ContextMenuProps {
  /** The actions to offer. */
  items: MenuItem[]
  /** The region that opens the menu on right-click or Shift+F10 / Menu key. */
  children: ReactNode
}

type MenuStyle = Pick<CSSProperties, 'position' | 'top' | 'left'>

// Estimated menu box used to clamp the open point so the menu stays on-screen
// before it has measured itself; corrected after native show.
const EST_W = 210
const EST_H = 280

function clampToViewport(x: number, y: number, w: number, h: number): MenuStyle {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0
  const M = 4
  let left = x
  let top = y
  if (vw > 0) left = Math.max(M, Math.min(x, vw - w - M))
  if (vh > 0) top = Math.max(M, Math.min(y, vh - h - M))
  return { position: 'fixed', top: Math.round(top), left: Math.round(left) }
}

/**
 * Right-click actions menu over a wrapped region.
 *
 * @example
 * <ContextMenu
 *   items={[
 *     { key: 'open', label: t('actions.open'), icon: 'eye', onSelect: open },
 *     { key: 'del', label: t('actions.delete'), tone: 'danger',
 *       separatorBefore: true, onSelect: remove },
 *   ]}
 * >
 *   <div className="case-row">…</div>
 * </ContextMenu>
 */
export function ContextMenu({ items, children }: ContextMenuProps) {
  const [open, setOpen] = useState(false)
  const [point, setPoint] = useState({ x: 0, y: 0 })
  const [menuStyle, setMenuStyle] = useState<MenuStyle>({ position: 'fixed', top: 0, left: 0 })
  // The currently active (focused) item index among the *enabled* items.
  const [activeIndex, setActiveIndex] = useState(0)

  const menuId = useId()
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  // Element to restore focus to when the menu closes.
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const ignoreDsCloseRef = useRef(false)

  // Indices of the items that can hold focus (skip disabled ones).
  const enabledIndices = items
    .map((item, i) => (item.disabled ? -1 : i))
    .filter((i) => i >= 0)

  const close = useCallback(() => setOpen(false), [])

  const openAt = useCallback(
    (clientX: number, clientY: number) => {
      if (items.length === 0) return
      if (!open) {
        restoreFocusRef.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null
      }
      setPoint({ x: clientX, y: clientY })
      setMenuStyle(clampToViewport(clientX, clientY, EST_W, EST_H))
      const first = items.findIndex((item) => !item.disabled)
      setActiveIndex(first >= 0 ? first : 0)
      setOpen(true)
    },
    [items, open],
  )

  const focusedPoint = useCallback(() => {
    const focused = document.activeElement
    const region = focused instanceof HTMLElement && anchorRef.current?.contains(focused)
      ? focused : anchorRef.current
    const rect = region?.getBoundingClientRect()
    return { x: rect?.left ?? 0, y: rect?.bottom ?? 0 }
  }, [])

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (e.defaultPrevented || menuRef.current?.contains(e.target as Node)) return
      e.preventDefault()
      e.stopPropagation()
      // Keyboard-generated contextmenu events may have no pointer coordinates.
      const point = e.button === 0 && e.clientX === 0 && e.clientY === 0
        ? focusedPoint() : { x: e.clientX, y: e.clientY }
      openAt(point.x, point.y)
    },
    [openAt, focusedPoint],
  )

  const onRegionKeyDown = useCallback((e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.defaultPrevented || menuRef.current?.contains(e.target as Node)) return
    if (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10')) return
    e.preventDefault()
    e.stopPropagation()
    const point = focusedPoint()
    openAt(point.x, point.y)
  }, [openAt, focusedPoint])

  // DS's document Escape handler does not check defaultPrevented. Observe the
  // bubbled event before its passive listener so inner handlers get first refusal.
  useLayoutEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      ignoreDsCloseRef.current = e.key === 'Escape' && e.defaultPrevented
    }
    const onClick = () => { ignoreDsCloseRef.current = false }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onClick, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onClick, true)
    }
  }, [])

  // Re-clamp against the menu's real measured size before paint, and keep it
  // pinned to the open point on scroll/resize.
  const reposition = useCallback(() => {
    const el = menuRef.current
    // Native popovers are display:none until DS's passive opening effect.
    if (!el?.matches(':popover-open')) return
    setMenuStyle(
      clampToViewport(point.x, point.y, el.offsetWidth, el.offsetHeight),
    )
  }, [point])

  useLayoutEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition])

  // Move focus into the active item on open / when the active index changes;
  // restore focus to the trigger region on close.
  useEffect(() => {
    if (open) {
      reposition()
      const item = itemRefs.current[activeIndex]
      ;(item && !item.disabled ? item : menuRef.current)?.focus()
    } else if (restoreFocusRef.current) {
      if (restoreFocusRef.current.isConnected) restoreFocusRef.current.focus()
      restoreFocusRef.current = null
    }
  }, [open, activeIndex, reposition])

  // Close on outside pointer-down (clicks inside the menu are kept open).
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return
      close()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [open, close])

  const moveActive = useCallback(
    (dir: 1 | -1) => {
      if (enabledIndices.length === 0) return
      const pos = enabledIndices.indexOf(activeIndex)
      const nextPos =
        pos < 0
          ? 0
          : (pos + dir + enabledIndices.length) % enabledIndices.length
      const next = enabledIndices[nextPos]
      if (next !== undefined) setActiveIndex(next)
    },
    [enabledIndices, activeIndex],
  )

  const select = useCallback(
    (index: number) => {
      const item = items[index]
      if (!item || item.disabled) return
      close()
      item.onSelect()
    },
    [items, close],
  )

  const onMenuKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.defaultPrevented) return
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          e.stopPropagation()
          moveActive(1)
          break
        case 'ArrowUp':
          e.preventDefault()
          e.stopPropagation()
          moveActive(-1)
          break
        case 'Home': {
          e.preventDefault()
          e.stopPropagation()
          const first = enabledIndices[0]
          if (first !== undefined) setActiveIndex(first)
          break
        }
        case 'End': {
          e.preventDefault()
          e.stopPropagation()
          const last = enabledIndices[enabledIndices.length - 1]
          if (last !== undefined) setActiveIndex(last)
          break
        }
        case 'Enter':
        case ' ':
          // Native buttons generate exactly one click. Keep row handlers out
          // of the event path without cancelling that default activation.
          e.stopPropagation()
          break
        case 'Escape':
          e.preventDefault()
          e.stopPropagation()
          close()
          break
        default:
          break
      }
    },
    [moveActive, enabledIndices, close],
  )

  itemRefs.current = []

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <DsDropdown
            ref={menuRef}
            id={menuId}
            open={open}
            placement="none"
            onClose={() => { if (!ignoreDsCloseRef.current) close() }}
            onToggle={reposition}
            className="dd__menu"
            role="menu"
            tabIndex={-1}
            style={menuStyle}
            onKeyDown={onMenuKeyDown}
            onClick={(e) => e.stopPropagation()}
          >
            <DsDropdown.List role="none">
              {items.map((item, index) => (
                <Fragment key={item.key}>
                  {item.separatorBefore && <DsDropdown.Item className="dd__sep" role="separator" />}
                  <DsDropdown.Item role="none">
                    <DsDropdown.Button
                      ref={(node) => {
                        itemRefs.current[index] = node
                      }}
                      role="menuitem"
                      className={clsx('dd__item', item.tone === 'danger' && 'dd__item--danger')}
                      disabled={item.disabled}
                      tabIndex={!item.disabled && index === activeIndex ? 0 : -1}
                      data-color={item.tone === 'danger' ? 'danger' : undefined}
                      onClick={() => select(index)}
                      onFocus={() => setActiveIndex(index)}
                      onMouseEnter={() => !item.disabled && setActiveIndex(index)}
                    >
                      {item.icon && <Icon name={item.icon} size={16} />}
                      <span className="dd__item-text">{item.label}</span>
                    </DsDropdown.Button>
                  </DsDropdown.Item>
                </Fragment>
              ))}
            </DsDropdown.List>
          </DsDropdown>,
          anchorRef.current?.closest('[popover],dialog') ?? document.body,
        )
      : null

  return (
    <span className="ctx-anchor" ref={anchorRef} onContextMenu={onContextMenu} onKeyDown={onRegionKeyDown}>
      {children}
      {menu}
    </span>
  )
}
